import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import type {
    ILeadRoutingRule,
    ILeadRoutingRuleDefault,
    ILeadRoutingRuleListingSiteNames,
    ILeadRoutingRuleProperties,
    ILeadRoutingRulePropertyLabels
} from 'src/services/api/lead-routing-rules/types';
import { LEAD_ROUTING_RULES_PATH } from 'src/services/api/lead-routing-rules/path';
import { getApiErrorMessage } from 'src/services/api/utils';

type ICreateLeadRoutingRuleResponse = {
    msg: string;
    rule: ILeadRoutingRule;
}

export type ICreateLeadRoutingRuleRequest =
    | Pick<ILeadRoutingRuleDefault, 'type' | 'target_landlord_user_id' | 'user_assigned_order'>
    | Pick<
        ILeadRoutingRuleProperties | ILeadRoutingRulePropertyLabels | ILeadRoutingRuleListingSiteNames,
        'type' | 'params' | 'target_landlord_user_id' | 'user_assigned_order'
    >;

export const useCreateLeadRoutingRule = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ICreateLeadRoutingRuleResponse,
        IStandardErrorResponse,
        ICreateLeadRoutingRuleRequest
    >(
        (payload) => {
            return apiClient(LEAD_ROUTING_RULES_PATH, {
                method: 'post',
                json: payload,
            }).json<ICreateLeadRoutingRuleResponse>();
        }, {
            onSuccess: () => {
                addNotification('Lead routing rule created successfully', 'success');
                queryClient.invalidateQueries(queryKeys.leadRoutingRules);
            }, onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to create lead routing rule';
                addNotification(errorMessage, 'error');
            }
        });
};
